import { capitalGlobalMinEffectiveStartDate } from '@/features/proposals/components/capital-global/utils/capital-global-effective-date-window.util';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import type { Proposal } from '@/services/proposals/types';
import type { CapitalGlobalProposalStepDadosDoSeguroValues } from '@/features/proposals/schemas/capital-global/proposal-step-dados-do-seguro.schema';
import { DateTime } from 'luxon';

function toDateOnly(value?: string) {
  return value ? value.split('T')[0] || value : '';
}

/**
 * Datas passadas são descartadas no prefill. Datas acima do teto de 30 dias
 * ficam no form de propósito: o bloqueio explícito da janela é mais claro que
 * um campo que apareceu vazio sem explicação.
 */
function resolveValidEffectiveStartDate(value?: string) {
  const dateOnly = toDateOnly(value);
  const date = DateTime.fromISO(dateOnly);
  const minDate = DateTime.fromISO(capitalGlobalMinEffectiveStartDate());

  if (!date.isValid || date < minDate) return '';
  return date.toISODate() || '';
}

type BillingLike = {
  zipCode?: string;
  cep?: string;
  address?: string;
  street?: string;
  addressNumber?: string;
  number?: string;
  addressComplement?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
};

type CapitalGlobalAddressLike = {
  zipCode?: string;
  street?: string;
  streetNumber?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
};

type CapitalGlobalProposalAddressFields = Pick<
  CapitalGlobalProposalStepDadosDoSeguroValues,
  | 'registrationCep'
  | 'registrationStreet'
  | 'registrationNumber'
  | 'registrationComplement'
  | 'registrationNeighborhood'
  | 'registrationCity'
  | 'registrationState'
  | 'sameCorrespondenceAsRegistration'
  | 'correspondenceCep'
  | 'correspondenceStreet'
  | 'correspondenceNumber'
  | 'correspondenceComplement'
  | 'correspondenceNeighborhood'
  | 'correspondenceCity'
  | 'correspondenceState'
>;

function mapCapitalGlobalAddressDtoToFormFields(
  address?: CapitalGlobalAddressLike
): Pick<
  CapitalGlobalProposalAddressFields,
  | 'registrationCep'
  | 'registrationStreet'
  | 'registrationNumber'
  | 'registrationComplement'
  | 'registrationNeighborhood'
  | 'registrationCity'
  | 'registrationState'
> {
  return {
    registrationCep: address?.zipCode || '',
    registrationStreet: address?.street || '',
    registrationNumber: address?.streetNumber || '',
    registrationComplement: address?.complement || '',
    registrationNeighborhood: address?.neighborhood || '',
    registrationCity: address?.city || '',
    registrationState: address?.state || '',
  };
}

function mapCorrespondenceAddressDtoToFormFields(
  address?: CapitalGlobalAddressLike
): Pick<
  CapitalGlobalProposalAddressFields,
  | 'correspondenceCep'
  | 'correspondenceStreet'
  | 'correspondenceNumber'
  | 'correspondenceComplement'
  | 'correspondenceNeighborhood'
  | 'correspondenceCity'
  | 'correspondenceState'
> {
  return {
    correspondenceCep: address?.zipCode || '',
    correspondenceStreet: address?.street || '',
    correspondenceNumber: address?.streetNumber || '',
    correspondenceComplement: address?.complement || '',
    correspondenceNeighborhood: address?.neighborhood || '',
    correspondenceCity: address?.city || '',
    correspondenceState: address?.state || '',
  };
}

function mapLegacyBillingToRegistration(
  billing?: BillingLike,
  customer?: Proposal['customer']
) {
  const cep = billing?.zipCode || billing?.cep || customer?.zipCode || '';
  const street = billing?.address || billing?.street || customer?.address || '';
  const addressNumber =
    billing?.addressNumber || billing?.number || customer?.addressNumber || '';
  const complement =
    billing?.addressComplement ||
    billing?.complement ||
    customer?.addressComplement ||
    '';
  const neighborhood = billing?.neighborhood || customer?.neighborhood || '';
  const city = billing?.city || customer?.city || '';
  const state = billing?.state || customer?.state || '';

  return {
    registrationCep: cep,
    registrationStreet: street,
    registrationNumber: addressNumber,
    registrationComplement: complement,
    registrationNeighborhood: neighborhood,
    registrationCity: city,
    registrationState: state,
  };
}

/**
 * Monta valores iniciais do form a partir da proposta carregada (uma vez no mount).
 */
export function mapProposalToCapitalGlobalStepDadosDoSeguroPrefill(
  proposal: Proposal
): Pick<
  CapitalGlobalProposalStepDadosDoSeguroValues,
  | 'customerPk'
  | 'cnpj'
  | 'companyName'
  | 'dateOfBirth'
  | 'gender'
  | 'effectiveStartDate'
  | 'registrationCep'
  | 'registrationStreet'
  | 'registrationNumber'
  | 'registrationComplement'
  | 'registrationNeighborhood'
  | 'registrationCity'
  | 'registrationState'
  | 'sameCorrespondenceAsRegistration'
  | 'correspondenceCep'
  | 'correspondenceStreet'
  | 'correspondenceNumber'
  | 'correspondenceComplement'
  | 'correspondenceNeighborhood'
  | 'correspondenceCity'
  | 'correspondenceState'
  | 'stipulatorPhone'
  | 'stipulatorEmail'
> & { registrationCepDigits: string } {
  const customer = proposal.customer;
  // Documento IDENTIFICADOR = `documentNumber` (CAEPF/CNPJ/CPF). Pós-split o `cpf`
  // guarda o CPF consultado/derivado — para CAEPF é OUTRO número (11 díg.), que
  // aparecia mascarado como CNPJ no lugar do CAEPF.
  const registerNumber =
    (customer as { documentNumber?: string })?.documentNumber ||
    customer?.cpf ||
    '';

  const capitalGlobalProposal = proposal as {
    registrationAddress?: CapitalGlobalAddressLike;
    correspondenceAddress?: CapitalGlobalAddressLike;
    correspondenceSameAsRegistration?: boolean;
    billingAddress?: BillingLike;
  };

  const sameCorrespondenceAsRegistration =
    capitalGlobalProposal.correspondenceSameAsRegistration ?? true;

  const registrationFromDto = mapCapitalGlobalAddressDtoToFormFields(
    capitalGlobalProposal.registrationAddress
  );
  const hasRegistrationDto = Boolean(
    capitalGlobalProposal.registrationAddress?.zipCode?.trim()
  );

  const legacyRegistration = mapLegacyBillingToRegistration(
    capitalGlobalProposal.billingAddress,
    customer
  );

  const registration = hasRegistrationDto
    ? registrationFromDto
    : legacyRegistration;

  const correspondence = sameCorrespondenceAsRegistration
    ? {
        correspondenceCep: '',
        correspondenceStreet: '',
        correspondenceNumber: '',
        correspondenceComplement: '',
        correspondenceNeighborhood: '',
        correspondenceCity: '',
        correspondenceState: '',
      }
    : mapCorrespondenceAddressDtoToFormFields(
        capitalGlobalProposal.correspondenceAddress
      );

  return {
    customerPk: customer?.pk || 0,
    cnpj: formatRegisterNumber(registerNumber),
    companyName: customer?.name || '',
    // CAEPF: pré-preenche data de nascimento e sexo do titular (quando já vieram).
    dateOfBirth: (customer?.dateOfBirth || '').split('T')[0] || '',
    gender: customer?.genderCode || '',
    effectiveStartDate: resolveValidEffectiveStartDate(proposal.effectiveDate),
    ...registration,
    sameCorrespondenceAsRegistration,
    ...correspondence,
    stipulatorPhone: customer?.cellphone || customer?.phone || '',
    stipulatorEmail: customer?.email || '',
    registrationCepDigits: registration.registrationCep.replace(/\D/g, ''),
  };
}
